import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'

import { Briefcase, Info, TriangleAlert } from 'lucide-react'

import { cn } from '@/lib/utils'

// GOV.UK: "You cannot work more than 48 hours a week on average", "normally
// averaged over 17 weeks", and "You can choose to work more by opting out".
const UK_LIMIT_HOURS = 48

// WHO/ILO joint release (17 May 2021): long working hours means "at least 55
// hours a week", compared with 35 to 40.
const WHO_LONG_HOURS = 55

const HOURS_IN_WEEK = 168

interface Field {
    key: FieldKey
    label: string
    hint: string
    unit: string
    min: number
    max: number
    step: number
}

type FieldKey = 'contracted' | 'extra' | 'days' | 'commute' | 'messages' | 'sleep'

const FIELDS: Field[] = [
    { key: 'contracted', label: 'Contracted hours', hint: 'What your contract or rota says', unit: 'hours a week', min: 0, max: 100, step: 0.5 },
    { key: 'extra', label: 'Extra hours', hint: 'Early starts, late finishes, weekend work', unit: 'hours a week', min: 0, max: 60, step: 0.5 },
    { key: 'days', label: 'Days you work', hint: 'Including any you work from home', unit: 'days a week', min: 1, max: 7, step: 1 },
    { key: 'commute', label: 'Commute', hint: 'There and back, on a working day. 0 if remote', unit: 'minutes a day', min: 0, max: 300, step: 5 },
    { key: 'messages', label: 'Work messages outside hours', hint: 'Email, chat and calls, on an average day', unit: 'minutes a day', min: 0, max: 300, step: 5 },
    { key: 'sleep', label: 'Sleep', hint: 'Used only to work out what is left', unit: 'hours a night', min: 3, max: 12, step: 0.5 }
]

type Values = Record<FieldKey, string>

const DEFAULTS: Values = {
    contracted: '37.5',
    extra: '4',
    days: '5',
    commute: '50',
    messages: '20',
    sleep: '7.5'
}

const read = (values: Values, field: Field): number | null => {
    const raw = values[field.key].trim()
    if (raw === '') return null
    const parsed = Number(raw)
    if (!Number.isFinite(parsed) || parsed < field.min || parsed > field.max) return null
    return parsed
}

const formatHours = (value: number): string => {
    const rounded = Math.round(value * 10) / 10
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

export const WorkingWeekCalculator: FC = (): ReactNode => {
    const [values, setValues] = useState<Values>(DEFAULTS)

    const parsed = Object.fromEntries(FIELDS.map((field) => [field.key, read(values, field)])) as Record<FieldKey, number | null>
    const invalid = FIELDS.filter((field) => parsed[field.key] === null)

    const set = (key: FieldKey, value: string) => setValues((previous) => ({ ...previous, [key]: value }))

    let result: ReactNode = null

    if (invalid.length === 0) {
        const contracted = parsed.contracted as number
        const worked = contracted + (parsed.extra as number)
        const commute = ((parsed.commute as number) * (parsed.days as number)) / 60
        const messages = ((parsed.messages as number) * 7) / 60
        const sleep = (parsed.sleep as number) * 7
        const workTotal = worked + commute + messages
        const beyondContract = workTotal - contracted
        const left = HOURS_IN_WEEK - sleep - workTotal

        const segments = [
            { key: 'sleep', label: 'Sleep', hours: sleep, className: 'bg-secondary' },
            { key: 'work', label: 'Working', hours: worked, className: 'bg-primary' },
            { key: 'commute', label: 'Commute', hours: commute, className: 'bg-primary/60' },
            { key: 'messages', label: 'Messages', hours: messages, className: 'bg-primary/35' },
            { key: 'left', label: 'Everything else', hours: Math.max(0, left), className: 'bg-background' }
        ]

        const status =
            worked >= WHO_LONG_HOURS
                ? `That is in the range WHO and the ILO call long working hours: ${WHO_LONG_HOURS} or more a week.`
                : worked > UK_LIMIT_HOURS
                    ? `That is over the ${UK_LIMIT_HOURS}-hour weekly average in UK working time rules, which people can opt out of.`
                    : `That is within the ${UK_LIMIT_HOURS}-hour weekly average in UK working time rules.`

        result = (
            <div>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                    <div
                        className={cn(
                            'rounded-xl border px-4 py-4',
                            worked > UK_LIMIT_HOURS ? 'border-primary/60 bg-primary/10' : 'border-primary/40 bg-primary/5'
                        )}
                    >
                        <span className='block text-sm font-medium text-foreground'>Hours you actually work</span>
                        <span className='mt-1 block font-heading text-3xl font-semibold tracking-tight text-foreground'>
                            {formatHours(worked)} a week
                        </span>
                        <span className='mt-1 flex gap-1.5 text-sm leading-relaxed text-muted-foreground'>
                            {worked > UK_LIMIT_HOURS && <TriangleAlert className='mt-0.5 size-4 shrink-0 text-primary' />}
                            {status}
                        </span>
                    </div>

                    <div className='rounded-xl border border-border bg-background px-4 py-4'>
                        <span className='block text-sm font-medium text-foreground'>Time work takes from your week</span>
                        <span className='mt-1 block font-heading text-3xl font-semibold tracking-tight text-foreground'>
                            {formatHours(workTotal)} hours
                        </span>
                        <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                            {formatHours(beyondContract)} hours a week beyond your contract, once the commute and
                            messages are counted. That is {formatHours(beyondContract * 52)} hours over a year of
                            weeks like this one.
                        </span>
                    </div>
                </div>

                <span className='mt-5 block text-sm font-medium text-foreground'>Your 168-hour week</span>
                <div className='mt-2 flex h-5 overflow-hidden rounded-full border border-border' aria-hidden='true'>
                    {segments.map((segment) =>
                        segment.hours > 0 ? (
                            <span
                                key={segment.key}
                                className={cn('h-full', segment.className)}
                                style={{ width: `${(segment.hours / HOURS_IN_WEEK) * 100}%` }}
                            />
                        ) : null
                    )}
                </div>
                <span className='mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3'>
                    {segments.map((segment) => (
                        <span key={segment.key} className='flex items-center gap-2 text-xs text-muted-foreground'>
                            <span className={cn('size-2.5 shrink-0 rounded-full border border-border', segment.className)} />
                            <span>
                                {segment.label}: <span className='font-medium text-foreground'>{formatHours(segment.hours)}h</span>
                            </span>
                        </span>
                    ))}
                </span>

                {left < 0 ? (
                    <span className='mt-4 flex gap-2 rounded-xl border border-primary/60 bg-primary/10 px-4 py-3 text-sm text-foreground'>
                        <TriangleAlert className='mt-0.5 size-4 shrink-0 text-primary' />
                        These numbers add up to more than the 168 hours in a week. Check the figures above.
                    </span>
                ) : (
                    <span className='mt-4 block rounded-xl border border-border bg-background px-4 py-3 text-sm leading-relaxed text-muted-foreground'>
                        That leaves <span className='font-semibold text-foreground'>{formatHours(left / 7)} hours a day</span>{' '}
                        for everything else: meals, chores, caring, getting ready, and whatever is left over
                        as real free time.
                    </span>
                )}
            </div>
        )
    }

    return (
        <section
            aria-label='Real working week calculator'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Briefcase className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Real working week calculator
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Add up the hours work really takes, not just the ones on your contract.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                    {FIELDS.map((field) => {
                        const bad = parsed[field.key] === null
                        return (
                            <div key={field.key}>
                                <label htmlFor={`working-week-${field.key}`} className='block text-sm font-medium text-foreground'>
                                    {field.label}
                                </label>
                                <div className='mt-2 flex items-center gap-2'>
                                    <input
                                        id={`working-week-${field.key}`}
                                        type='number'
                                        inputMode='decimal'
                                        min={field.min}
                                        max={field.max}
                                        step={field.step}
                                        value={values[field.key]}
                                        onChange={(event) => set(field.key, event.target.value)}
                                        aria-invalid={bad}
                                        aria-describedby={`working-week-${field.key}-hint`}
                                        className={cn(
                                            'w-28 rounded-xl border bg-background px-3 py-2.5 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                            bad ? 'border-primary' : 'border-border'
                                        )}
                                    />
                                    <span className='text-sm text-muted-foreground'>{field.unit}</span>
                                </div>
                                <span id={`working-week-${field.key}-hint`} className='mt-1 block text-xs text-muted-foreground'>
                                    {bad ? `Enter a number from ${field.min} to ${field.max}.` : field.hint}
                                </span>
                            </div>
                        )
                    })}
                </div>

                <div aria-live='polite' className='mt-6'>
                    {result ?? (
                        <span className='block rounded-xl border border-border bg-background px-4 py-4 text-sm text-muted-foreground'>
                            Fill in every box to see your real working week.
                        </span>
                    )}
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        The legal limit applies to working time as the rules define it, so the commute and
                        messages are shown separately rather than counted towards it. Nothing you enter is
                        stored. See{' '}
                        <Link href='/work-life-balance/long-working-hours-health' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            do long working hours actually harm you?
                        </Link>
                    </span>
                </div>
            </div>
        </section>
    )
}

export default WorkingWeekCalculator
